"use client";
import React from "react";
import { Button } from "@/components/ui/button";
import { SectionContainer } from "./layouts";
import { useWebViewAuthModal } from "@/hooks/client/useWebViewAuthModal";

const PointsManagementSection: React.FC<{ className?: string }> = ({
  className,
}) => {
  const { handleClick, AuthModals } = useWebViewAuthModal();
  return (
    <>
      <div className="flex w-full flex-col">
        {/* Top Section with Full-Width Gradient Background */}
        <div
          className="relative h-[435px] w-full lg:h-[600px]"
          style={{
            background:
              "linear-gradient(359deg, hsl(var(--pure-white)) 5.34%, hsl(var(--pure-white) / 0.19) 38.65%, hsl(var(--pure-white) / 0) 83.78%), radial-gradient(152.59% 92.12% at 50% 92.12%, hsl(var(--pure-white)) 0%, hsl(var(--points-management-radial-mid)) 32.69%, hsl(var(--points-management-radial-base)) 61.06%, #020817 100%)",
          }}
        >
          {/* Content */}
          <div className="absolute inset-0 flex flex-col items-center justify-center overflow-visible">
            <div className="inline-flex w-full max-w-[580px] flex-col items-center justify-start gap-6 overflow-visible">
              {/* Static dark text — sits on inline-style gradient that fades to #FFF */}
              <div className="w-full text-center font-figtree text-2xl font-semibold leading-6 text-pure-black lg:text-3xl lg:leading-8">
                The average traveler finds
              </div>
              <div className="flex w-full items-center justify-center">
                <div className="inline-block bg-gradient-to-l from-gondola-blue-950 to-gondola-blue-700 bg-clip-text px-2 text-center font-figtree text-8xl font-extrabold leading-none text-transparent lg:text-9xl lg:leading-tight">
                  $3,125
                </div>
              </div>
              {/* Static dark text — sits on inline-style gradient that fades to #FFF */}
              <div className="w-full text-center font-figtree text-2xl font-semibold leading-6 text-pure-black lg:text-3xl lg:leading-8">
                in points using Gondola.
              </div>
            </div>
          </div>
        </div>

        {/* Middle Section */}
        <SectionContainer background="light" className={className} headerInvert>
          <div className="flex w-full flex-col items-center justify-center gap-12">
            {/* Top Part */}
            <div className="inline-flex w-full max-w-[536px] flex-col items-center justify-start gap-6">
              {/* Static dark text — inside SectionContainer background="light" */}
              <div className="w-full text-balance text-center font-figtree text-4xl font-semibold leading-9 text-gondola-blue-950 lg:text-6xl lg:leading-none">
                Travel like a pro
              </div>
              <div className="w-full max-w-[420px] text-center font-figtree text-base font-normal leading-6 text-pure-black/60 lg:text-xl lg:font-medium lg:leading-7">
                You&apos;ve got places to be,
                <br />
                let Gondola AI work for you.
              </div>
            </div>

            {/* Cards */}
            <div className="flex w-full flex-col items-center justify-center gap-4 lg:h-[293px] lg:flex-row">
              {/* Left Card — static light section */}
              <div className="flex w-full flex-1 flex-col items-start justify-start gap-6 overflow-hidden rounded-lg border border-pure-black/10 bg-landing-cream/80 p-6 lg:h-full">
                {/* Supported connection methods */}
                <div className="flex h-16 items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pure-white p-2">
                    <img
                      className="h-full w-full object-contain"
                      src="/assets/logos/google.svg"
                      alt="Gmail"
                      width={48}
                      height={48}
                      loading="lazy"
                    />
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pure-white p-2">
                    <img
                      className="h-full w-full object-contain"
                      src="/assets/logos/microsoft.svg"
                      alt="Outlook"
                      width={48}
                      height={48}
                      loading="lazy"
                    />
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pure-white p-2">
                    <img
                      className="h-full w-full object-contain"
                      src="/assets/logos/yahoo.svg"
                      alt="Yahoo Mail"
                      width={48}
                      height={48}
                      loading="lazy"
                    />
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-pure-white p-2">
                    <img
                      className="h-full w-full object-contain"
                      src="/assets/awardwallet/logo.png"
                      alt="AwardWallet"
                      width={48}
                      height={48}
                      loading="lazy"
                    />
                  </div>
                </div>

                {/* Title and Description */}
                <div className="flex w-full flex-col items-start justify-start gap-2">
                  <div className="w-full font-figtree text-xl font-medium leading-7 text-pure-black">
                    Link your account
                  </div>
                  <div className="w-full font-figtree text-base font-medium leading-6 text-pure-black/60">
                    We&apos;ll automatically gather all your points and trips in
                    one place.
                  </div>
                </div>
              </div>

              {/* Middle Card — static light section */}
              <div className="flex w-full flex-1 flex-col items-start justify-start gap-6 overflow-hidden rounded-lg border border-pure-black/10 bg-landing-cream/80 p-6 lg:h-full">
                {/* Reward Program Labels */}
                <div className="inline-flex min-h-16 w-full flex-wrap content-start items-start justify-start gap-2">
                  {/* eslint-disable-next-line local-rules/enforce-semantic-colors -- decorative landing page tier badge illustrations */}
                  <div className="flex items-center justify-center gap-3 rounded bg-gradient-to-l from-[#dee6e9] to-[#a1b4c0] px-2 py-1 outline outline-[1.20px] outline-[#566c81]">
                    <div className="text-center font-figtree text-sm font-medium leading-5 text-pure-black/70">
                      UNITED
                    </div>
                  </div>
                  {/* eslint-disable-next-line local-rules/enforce-semantic-colors -- decorative landing page tier badge illustrations */}
                  <div className="flex items-center justify-center gap-3 rounded bg-gradient-to-l from-[#cfe4ed] to-[#91bcd6] px-2 py-1 outline outline-[1.20px] outline-[#406d96]">
                    <div className="text-center font-figtree text-sm font-medium leading-5 text-pure-black/80">
                      IHG ONE REWARDS
                    </div>
                  </div>
                  {/* eslint-disable-next-line local-rules/enforce-semantic-colors -- decorative landing page tier badge illustrations */}
                  <div className="flex items-center justify-center gap-3 rounded bg-gradient-to-l from-[#f3d7d7] to-[#d99a9a] px-2 py-1 outline outline-[1.20px] outline-[#963d3d]">
                    <div className="text-center font-figtree text-sm font-medium leading-5 text-pure-black/80">
                      AVIS
                    </div>
                  </div>
                  {/* eslint-disable-next-line local-rules/enforce-semantic-colors -- decorative landing page tier badge illustrations */}
                  <div className="flex items-center justify-center gap-3 rounded bg-gradient-to-l from-[#f9e7bf] to-[#ecc475] px-2 py-1 outline outline-[1.20px] outline-[#d88e0c]">
                    <div className="text-center font-figtree text-sm font-medium leading-5 text-pure-black/80">
                      MARRIOTT BONVOY
                    </div>
                  </div>
                  {/* eslint-disable-next-line local-rules/enforce-semantic-colors -- decorative landing page tier badge illustrations */}
                  <div className="flex items-center justify-center gap-3 rounded bg-gradient-to-l from-[#cfe0d0] to-[#9dc2a4] px-2 py-1 outline outline-[1.20px] outline-[#3f6d4b]">
                    <div className="text-center font-figtree text-sm font-medium leading-5 text-pure-black/80">
                      AMERICAN EXPRESS
                    </div>
                  </div>
                </div>

                {/* Title and Description */}
                <div className="flex w-full flex-col items-start justify-start gap-2">
                  <div className="w-full font-figtree text-xl font-medium leading-7 text-pure-black">
                    See all your points and trips
                  </div>
                  <div className="w-full font-figtree text-base font-medium leading-6 text-pure-black/60">
                    Easily manage all of your loyalty points and upcoming travel
                    in one place.
                  </div>
                </div>
              </div>

              {/* Right Card — static light section */}
              <div className="flex w-full flex-1 flex-col items-start justify-start gap-6 overflow-hidden rounded-lg border border-pure-black/10 bg-landing-cream/80 p-6 lg:h-full">
                <div className="h-16 w-16">
                  <img
                    className="h-full w-full object-contain"
                    src="/assets/landing-page/logo-gold.png"
                    alt="Gondola logo"
                    width={192}
                    height={192}
                    loading="lazy"
                  />
                </div>

                {/* Title and Description */}
                <div className="flex w-full flex-col items-start justify-start gap-2">
                  <div className="w-full font-figtree text-xl font-medium leading-7 text-pure-black">
                    Book the best deals, and
                    <br />
                    earn the most points
                  </div>
                  <div className="w-full font-figtree text-base font-medium leading-6 text-pure-black/60">
                    Book your member rates, earn your direct points, and stack
                    3-7% back.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button — `mb-8` gives the button's own drop shadow
              (0px 4px 25px) room to render against this section's white
              background before it hits the section's `pb-0` edge (kept
              zero deliberately, for the seamless transition into the next
              gradient section below) and the shadow gets visually chopped. */}
          <div className="mb-8 mt-16 flex w-full justify-center">
            <Button
              variant="orange-gradient"
              // The variant's fixed `h-11` is shorter than this text's own
              // `text-lg`/`leading-7` line height plus the variant's
              // vertical padding, so `overflow-hidden` was clipping it —
              // `h-auto` lets the button size to its content instead.
              className="h-auto font-figtree text-lg leading-7 text-pure-white"
              onClick={handleClick}
            >
              See what my points are worth
            </Button>
          </div>
        </SectionContainer>
      </div>
      <AuthModals />
    </>
  );
};

export default PointsManagementSection;
